import { useSelector } from 'react-redux';
import { RootState } from '@Redux/search/store';
import { useEffect, useMemo } from 'react';
import { triggerDataLayerEvent, ItemCategory } from '@libs/dataLayer';
import type { I_CardsInfo_Common } from '@Redux/search/interfaces';
import { useMediaQuery } from 'react-responsive';

export default function useDataLayer() {
    const { AllCardsInfo, TagList } = useSelector((state: RootState) => state.search.reducer);
    const DataList = AllCardsInfo.DataList as ({ SaleCurr?: string } & I_CardsInfo_Common)[];
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });

    const filterData = useFilterData(TagList);
    const hasFilter = hasFilterSort(filterData);
    const { Page, PageSize } = filterData;

    const KEYWORD = window.sessionStorage.getItem('Keyword');

    function calcDiscount(tradePrice: number, directPrice: number) {
        if (!tradePrice || !directPrice) return 0;
        const discount = directPrice - tradePrice;
        return +((discount / directPrice) * 100).toFixed(2);
    }

    useEffect(() => {
        // 無關鍵字時一樣啟動GA
        if (!KEYWORD) return sendEmptyDataLayer();
        // 篩選和排序不應觸發
        if (hasFilter || DataList.length === 0) return;

        // * M 版 - 根據筆數判斷頁碼，並只送出對應頁碼的資料
        // const data = isMobile ? DataList.slice((PageSize / 10 - 1) * 10) : DataList;

        const dataToSend = DataList.map((item, i) => ({
            item_id: item.ProductID, //  產品編號
            item_name: item.ProductName, // 產品名稱
            affiliation: '', // 應商代碼
            discount: calcDiscount(item.TradePrice, item.DirectPrice), // 商品折扣
            index: isMobile ? i + 1 : (Page - 1) * 10 + i + 1, // 第幾筆資料
            // index: (Page - 1) * 10 + i + 1, // *第幾筆資料 (使用為了 M 版處理的 data 時改使用這條 )
            item_brand: 'Lion Travel', // 品牌 (雄獅 Lion Travel || 雙獅 Uplan Travel)
            item_category: getCategory(TagList, item) as ItemCategory, // 商品類別
            price: item.TradePrice ?? '', //  售價 (單價) *依列表金額傳值
            item_list_name: 'AllSearch', // 商品列表名稱(全產品用)
            currency: item.SaleCurr ?? 'TWD', // 幣別
        }));

        triggerDataLayerEvent('view_item_list', {
            items: dataToSend,
        });
    }, [DataList]);
}

function useFilterData(tag: string) {
    const { GroupFilterSelected } = useSelector((state: RootState) => state.search.GroupReducer);
    const { HotelFilterSelected } = useSelector((state: RootState) => state.search.HotelReducer);
    const { TicketFilterSelected } = useSelector((state: RootState) => state.search.TicketReducer);
    const { VocationFilterSelected } = useSelector(
        (state: RootState) => state.search.VocationReducer
    );
    const { FlightFilterSelected } = useSelector((state: RootState) => state.search.FlightReducer);
    const { AllFilterSelected } = useSelector((state: RootState) => state.search.AllReducer);

    const curFilterData = useMemo(() => {
        switch (tag) {
            case 'grp':
                return GroupFilterSelected;
            case 'htl':
                return HotelFilterSelected;
            case 'vac':
                return VocationFilterSelected;
            case 'etk':
                return TicketFilterSelected;
            case 'flt':
                return FlightFilterSelected;
            default:
                return AllFilterSelected;
        }
    }, [
        tag,
        GroupFilterSelected,
        HotelFilterSelected,
        TicketFilterSelected,
        VocationFilterSelected,
        FlightFilterSelected,
        AllFilterSelected,
    ]);

    return curFilterData;
}

function hasFilterSort(
    filterData: { [key: string]: any },
    excludeKey: string[] = ['Page', 'PageSize', 'selectedProductType']
) {
    let noFilter = Object.keys(filterData).every((key) => {
        if (excludeKey.includes(key)) return true; // 並非篩選
        if (!filterData[key]) return true; // 為 '' 或 null
        if (Array.isArray(filterData[key]) && filterData[key].length === 0) return true; // 為 []
        return false;
    });

    if (filterData['selectedProductType']?.length < 4) noFilter = false; //預設全選

    return !noFilter;
}

function getCategory(tag: string, itemData: I_CardsInfo_Common) {
    const isForeign = true; // 目前無法判斷國內外統一使用國外

    function helper(tag: string) {
        switch (tag) {
            case 'grp':
                return isForeign ? 'B2CWebpd' : 'B2CWebtwpd';
            case 'htl':
                return isForeign ? 'B2CWebht' : 'B2CWebtwht';
            case 'vac':
                return isForeign ? 'B2CWebvacation' : 'B2CWebtwpd';
            case 'etk':
                return isForeign ? 'B2CWebet' : 'B2CWebtwet';
            case 'flt':
                return isForeign ? 'B2CWebptk' : 'B2CWebtwptk';
            default:
                // 全產品資料會給類別，改傳入 ProductType
                return undefined;
        }
    }

    return helper(tag) ?? helper(itemData.ProductType);
}

export function sendEmptyDataLayer() {
    const dataToSend = {
        item_id: '', //  產品編號
        item_name: '', // 產品名稱
        affiliation: '', // 應商代碼
        discount: '', // 商品折扣
        index: '', // 第幾筆資料
        item_brand: 'Lion Travel', // 品牌 (雄獅 Lion Travel || 雙獅 Uplan Travel)
        item_category: '', // 商品類別
        price: '', //  售價 (單價) *依列表金額傳值
        item_list_name: 'AllSearch', // 商品列表名稱(全產品用)
        currency: 'TWD', // 幣別
    } as const;

    triggerDataLayerEvent('view_item_list', {
        items: [dataToSend],
    });
}
